// An app's business approval (AG-29): one or two ordered steps, each with its
// own assignee. Only the person whose step is current can decide, and only
// that step. Approving here never authorizes a new change outside Poneva; the
// card says so.
import { Callout } from '@/components/ui/content';
import { StatusPill } from '@/components/ui/kit';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appApprovalsCopy from '@/i18n/ns/app-approvals';
import { useDecisionsApi, type AppDecisionsApi } from './app-decisions-api';
import type { AppDecision, BusinessApproval, BusinessStep } from './app-decision-types';
import {
  BlockedLine,
  NoticeLine,
  OriginLine,
  UnknownBlock,
  decisionView,
  isDecidable,
  statusLabel,
  statusTone,
} from './approval-parts';
import { useAppDecision } from './use-app-decision';
import './app-cards.css';

/** The step that is waiting on a person: the first one still pending. */
export const currentStep = (steps: readonly BusinessStep[]) =>
  steps.find((step) => step.state === 'pending') ?? null;

export function BusinessApprovalCard({
  id,
  initial = null,
  api: explicitApi,
  onSettled,
}: {
  id: string;
  initial?: BusinessApproval | null;
  api?: AppDecisionsApi;
  onSettled?: (record: AppDecision) => void;
}) {
  const copy = useNamespaceCopy(appApprovalsCopy);
  const api = useDecisionsApi(explicitApi);
  const { record, loadFailed, busy, lost, stillOpen, notice, decide, checkStatus } = useAppDecision(
    api,
    'business',
    id,
    initial,
    onSettled,
  );
  const approval = record?.kind === 'business' ? record : null;
  if (!approval)
    return (
      <Callout tone={loadFailed ? 'danger' : 'info'}>
        {loadFailed ? copy.loadFailed : copy.loading}
      </Callout>
    );
  const view = decisionView(approval, lost);
  const current = currentStep(approval.steps);
  const decidable = isDecidable(approval, lost) && current?.isYou === true;
  const stepLabel = {
    waiting: copy.stepWaiting,
    pending: copy.stepPending,
    approved: copy.stepApproved,
    rejected: copy.stepRejected,
  };
  return (
    <section className="ws-app-card" data-card="app-business" data-view={view}>
      <header className="ws-app-card-head">
        <div>
          <h3>{approval.title}</h3>
          <OriginLine copy={copy} origin={approval.origin} forName={approval.requesterName} />
        </div>
        <StatusPill tone={statusTone(view)}>{statusLabel(copy, view)}</StatusPill>
      </header>
      {approval.detail && <p className="ws-app-card-note">{approval.detail}</p>}
      <BlockedLine copy={copy} block={approval} />
      {view === 'unknown' && (
        <UnknownBlock
          copy={copy}
          busy={busy}
          still={stillOpen}
          onCheck={() => void checkStatus()}
        />
      )}
      <NoticeLine copy={copy} notice={notice} />
      <ol className="ws-app-steps">
        {approval.steps.map((step) => (
          <li
            key={step.order}
            data-step-state={step.state}
            aria-current={step === current ? 'step' : undefined}
          >
            <span className="ws-app-step-order">
              {formatCopy(copy.businessStep, { order: step.order })}
            </span>
            <span className="ws-app-step-who">{step.isYou ? copy.stepYou : step.assigneeName}</span>
            <span className="ws-app-step-state">{stepLabel[step.state]}</span>
          </li>
        ))}
      </ol>
      {view === 'pending' && current && !current.isYou && (
        <p className="ws-app-card-note">
          {formatCopy(copy.businessNotYourTurn, { name: current.assigneeName })}
        </p>
      )}
      {decidable && <p className="ws-app-card-note">{copy.businessYourTurn}</p>}
      <p className="ws-app-card-note">{copy.businessNote}</p>
      {decidable && current && (
        <div className="ws-app-card-actions">
          <button
            type="button"
            className="ws-button primary"
            disabled={busy}
            onClick={() => void decide({ decision: 'approve', stepOrder: current.order })}
          >
            {busy ? copy.working : copy.approve}
          </button>
          <button
            type="button"
            className="ws-button quiet"
            disabled={busy}
            onClick={() => void decide({ decision: 'reject', stepOrder: current.order })}
          >
            {copy.reject}
          </button>
        </div>
      )}
    </section>
  );
}
